/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDate, LocalDateTime } from 'js-joda';
import { oc } from 'ts-optchain';
import _ from 'lodash';
import supportedCountries from '@tock/shared/utils/supportedCountries';

import { ConsumerPurchaseSummaryFragment } from 'generated/graphql';
import {
  DEFAULT_FAVORITES_PLAYLIST,
  DEFAULT_STARRED_PLAYLIST,
  DEFAULT_WANT_TO_GO_PLAYLIST,
} from 'selectors/patronSelectors';
import { consumer, core, safeToNumber } from '@tock/coretypes';

/**
 * Any purchase matching today's date is considered upcoming.
 * Ignore the time.
 * @param histories The purchase details.
 * @returns the list of upcoming bookings.
 */
export function getUpcomingBookings<
  T extends {
    cancelledOrRefunded?: boolean | null;
    firstTransferredTo?: core.IPatron | null;
    ticketDateTime?: string | null;
  },
>(histories: T[] | null | undefined, patronEmail: string): T[] {
  if (!histories) {
    return [];
  }
  return _.filter(histories, (history) => {
    // For upcoming bookings we filter out cancelled and transferred out bookings.
    if (history.cancelledOrRefunded) {
      return false;
    }
    // Legacy transfers have purchase.firstTransferredTo set.
    if (history.firstTransferredTo && history.firstTransferredTo.email !== patronEmail) {
      return false;
    }
    const date = getTicketDate(history);
    const today = LocalDate.now();
    return date.isAfter(today) || date.isEqual(today);
  });
}

export function getTicketDate(history: { ticketDateTime?: string | null }) {
  return LocalDateTime.parse(history?.ticketDateTime || '').toLocalDate();
}

/** Returns a non-zero number as a string, or the empty string. */
function numberAsStringOrEmpty(datePart?: number | null) {
  return _.isNumber(datePart) && datePart !== 0 ? datePart.toString() : '';
}

interface Birthdate {
  birthdayDay: string;
  birthdayMonth: string;
  birthdayYear: string;
}

/** Returns empty strings if the day is not saved or any part of the date isn't present. */
export function getBirthdateFromProfile(patronProfile: core.PatronProfile): Birthdate {
  if (patronProfile.day[0] && patronProfile.day[0].type === 'BIRTHDAY') {
    const birthdate = patronProfile.day[0];
    return {
      birthdayDay: numberAsStringOrEmpty(birthdate.day),
      birthdayMonth: numberAsStringOrEmpty(birthdate.month),
      birthdayYear: numberAsStringOrEmpty(birthdate.year),
    };
  }
  return {
    birthdayDay: '',
    birthdayMonth: '',
    birthdayYear: '',
  };
}

interface AsyncValidatePhoneConfig {
  phoneFieldName: string;
  countryCodeFieldName: string;
}

export function asyncValidatePhone(options: AsyncValidatePhoneConfig) {
  const { countryCodeFieldName, phoneFieldName } = options;
  return (values: any, dispatch: Dispatch, props: any) => {
    if (_.isNil(values[phoneFieldName]) && _.isNil(values[countryCodeFieldName])) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      props
        .onValidatePhoneRequest({
          phone: values[phoneFieldName],
          phoneCountryCode: values[countryCodeFieldName],
        })
        .then((response: ProtoResponse<any>) => {
          const validatePhoneResponse = response.payload;
          if (validatePhoneResponse.isValid || props.form._submitFailed) {
            resolve();
          } else {
            reject({
              [phoneFieldName]: 'Not a valid phone number for that country',
            });
          }
        })
        .catch(() => {
          reject({
            [phoneFieldName]: 'Could not validate the phone number',
          });
        });
    });
  };
}

export function getReservationLink(
  reservation: core.IPastPurchase | consumer.IPurchaseSummary | ConsumerPurchaseSummaryFragment
): string | undefined {
  if (reservation.business && reservation.business.id) {
    return `/profile/reservations/${safeToNumber(reservation.id)}`;
  }
  return undefined;
}

// Get country for city destinations
export function getCountryFromMetro(destination: core.IMetroArea | core.MetroArea) {
  const splitLocale = destination.locale ? destination.locale.split('-') : [''];
  const localeCountry = splitLocale[splitLocale.length - 1].toUpperCase();

  return localeCountry && oc(supportedCountries)[localeCountry].geocodedName();
}

export type PlaylistType = 'STAR' | 'WANT_TO_GO' | 'FAVORITE' | 'CUSTOM';

export const getPlaylistType = (playlist: consumer.Playlist): PlaylistType => {
  if (playlist.name === DEFAULT_STARRED_PLAYLIST) {
    return 'STAR';
  } else if (playlist.name === DEFAULT_WANT_TO_GO_PLAYLIST) {
    return 'WANT_TO_GO';
  } else if (playlist.name === DEFAULT_FAVORITES_PLAYLIST) {
    return 'FAVORITE';
  } else {
    return 'CUSTOM';
  }
};
